import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Box, Typography, Chip, IconButton, Tooltip, CircularProgress,
  Alert, Dialog, DialogContent,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import LinkIcon from '@mui/icons-material/Link';
import { TableKit } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import ImportSection from '../../components/ImportSection.jsx';
import KolelCard from './KolelCard.jsx';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useQuickCreate } from '../../next/quickCreate';
import { ckConfirm } from '@NP-Webber/crm-kit/dist/next/index.js';

const STATUS_LABELS = { active: 'פעיל', inactive: 'לא פעיל' };
const SCHEDULE_LABELS = {
  'לפנה"צ':  'לפנה"צ',
  'אחה"צ':   'אחה"צ',
  'ערב':     'ערב',
  'כל היום': 'כל היום',
};

const COLUMNS = [
  { key: 'code',          title: 'קוד',          sortable: true,  filterable: true,  minWidth: '60px' },
  { key: 'name',          title: 'שם כולל',       sortable: true,  filterable: true,  minWidth: '140px' },
  { key: 'city',          title: 'עיר',           sortable: true,  filterable: true,  minWidth: '90px' },
  {
    key: 'schedule', title: 'סדר', sortable: true, filterable: true, minWidth: '80px',
    render: (v) => v || '—',
  },
  { key: 'subject',       title: 'מקצוע',         sortable: true,  filterable: true,  minWidth: '100px', render: (v) => v || '—' },
  { key: 'headFirstName', title: 'שם ראש כולל', sortable: true, filterable: true, minWidth: '110px',
    render: (v) => v || '—' },
  { key: 'headLastName',  title: 'משפחה',       sortable: true, filterable: true, minWidth: '90px',
    render: (v) => v || '—' },
  { key: 'gabaiName',     title: 'גבאי',          sortable: true,  filterable: true,  minWidth: '110px', render: (v) => v || '—' },
  { key: 'gabaiPhone',    title: 'טלפון גבאי',     sortable: true,  filterable: true,  minWidth: '110px', render: (v) => v || '—' },
  { key: 'memberCount',   title: 'אברכים',        sortable: false, filterable: false, minWidth: '70px',  render: (v) => v || '—' },
  {
    key: 'status', title: 'סטטוס', sortable: true, filterable: true, minWidth: '80px',
    render: (v) => (
      <Chip
        label={STATUS_LABELS[v] || v}
        size="small"
        color={v === 'active' ? 'success' : 'default'}
        variant={v === 'active' ? 'filled' : 'outlined'}
        sx={{ fontWeight: 600 }}
      />
    ),
  },
];

const KolelsList = () => {
  const navigate = useNavigate();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioElRef = useRef(null);
  const fileInputRefs = useRef({});
  const [selectedId, setSelectedId] = useState(null);
  useQuickCreate(() => setSelectedId('new'));

  // Link kolels
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [linkError, setLinkError] = useState('');

  const handleLinkKolels = async () => {
    setLinkLoading(true);
    setLinkResult(null);
    setLinkError('');
    try {
      const { data } = await api.post('/import/link-kolels');
      setLinkResult(data);
    } catch (err) {
      setLinkError(err.response?.data?.error || 'שגיאה בקישור כוללים');
    } finally {
      setLinkLoading(false);
    }
  };
  const handlePlay = useCallback((kolelId) => {
    // אם כבר מנגן את אותו כולל — עצור
    if (playingId === kolelId && audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
      setPlayingId(null);
      return;
    }
    // עצור נגינה קודמת
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
    // הבא את השמע דרך ה-API (עם אותנטיקציה)
    api.get(`/kolels/${kolelId}/audio`, { responseType: 'blob' })
      .then(({ data: blob }) => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); audioElRef.current = null; };
        audio.onerror = () => { setPlayingId(null); URL.revokeObjectURL(url); audioElRef.current = null; };
        audioElRef.current = audio;
        setPlayingId(kolelId);
        audio.play();
      })
      .catch(() => { setPlayingId(null); });
  }, [playingId]);

  const handleAudioUpload = useCallback(async (kolelId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('audio', file);
    try {
      setUploadingId(kolelId);
      const { data: res } = await api.post(`/kolels/${kolelId}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAllData((prev) =>
        prev.map((r) => r._id === kolelId
          ? { ...r, audioFilename: res.audioFilename, audio: r.audio || 'synced' }
          : r
        )
      );
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בהעלאת קובץ שמע');
    } finally {
      setUploadingId(null);
      if (fileInputRefs.current[kolelId]) fileInputRefs.current[kolelId].value = '';
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/kolels', { params: { page: 1, pageSize: 100000 } });
      setAllData(res.data);
    } catch (err) {
      console.error('שגיאה בשליפת כוללים:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = useCallback(async (row) => {
    if (!(await ckConfirm({ title: 'מחיקת כולל', message: `למחוק את הכולל "${row.name}"?`, confirmLabel: 'מחיקה' }))) return;
    try {
      await api.delete(`/kolels/${row._id}`);
      setAllData((prev) => prev.filter((r) => r._id !== row._id));
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה במחיקה');
    }
  }, []);

  const columnsWithActions = [
    ...COLUMNS,
    {
      key: 'audioFilename', title: 'קובץ שמע', sortable: false, filterable: false, minWidth: '180px',
      render: (v, row) => {
        const isUploading = uploadingId === row._id;
        const isPlaying = playingId === row._id;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {v ? (
              <>
                <Tooltip title={isPlaying ? 'עצור' : 'השמע'}>
                  <IconButton
                    size="small"
                    color={isPlaying ? 'error' : 'success'}
                    onClick={() => handlePlay(row._id)}
                  >
                    {isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" noWrap sx={{ maxWidth: 80 }} title={v}>
                  {v}
                </Typography>
              </>
            ) : null}
            <Tooltip title={v ? 'החלף קובץ' : 'העלה קובץ שמע'}>
              <IconButton
                size="small"
                color={v ? 'default' : 'primary'}
                component="label"
                disabled={isUploading}
              >
                {isUploading
                  ? <CircularProgress size={16} />
                  : <UploadFileIcon fontSize="small" />}
                <input
                  type="file"
                  accept="audio/*"
                  hidden
                  ref={(el) => { fileInputRefs.current[row._id] = el; }}
                  onChange={(e) => handleAudioUpload(row._id, e.target.files[0])}
                />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      key: '_actions', title: 'פעולות', sortable: false, filterable: false, minWidth: '90px',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="ערוך">
            <IconButton size="small" onClick={() => setSelectedId(row._id)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="מחק">
            <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <AdminPageHeader
        title="כוללים"
        sub={!loading ? `${allData.length.toLocaleString('he-IL')} כוללים` : undefined}
        primary={{ label: 'כולל חדש', onClick: () => setSelectedId('new') }}
        legacy={(
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>כוללים</Typography>
            <Button variant="contained" color="secondary" onClick={() => setSelectedId('new')}>
              + כולל חדש
            </Button>
          </Box>
        )}
      />

      {/* כרטיס כולל — מודל צף */}
      <Dialog
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <KolelCard
            recordId={selectedId}
            onClose={(reason) => {
              setSelectedId(null);
              if (reason === 'created') loadData();
            }}
          />
        </DialogContent>
      </Dialog>

      <TableKit
        columns={columnsWithActions}
        data={allData}
        total={allData.length}
        loading={loading}
        clientSideMode
        emptyMessage="לא נמצאו כוללים"
        exportFileName="כוללים"
        onOpenCard={(row) => setSelectedId(row._id)}
      />

      <ImportSection
        label="כוללים"
        endpoint="/import/kolels"
        templateEndpoint="/import/template/kolels"
        description="ייבוא רשימת כוללים. עמודות: שם כולל, עיר, קוד, שם ראש כולל, משפחה, סדר, מקצוע, סימנים, שם גבאי, טלפון גבאי. ניתן גם להעלות קובץ ישן עם שורות הפרדה לפי עיר."
        onImportDone={() => loadData()}
        entityType="kolels"
        identifierColumns={[
          { field: 'code', label: 'קוד כולל' },
          { field: 'name', label: 'שם כולל' },
        ]}
        extraActions={
          <Box mt={2} sx={{ p: 2, border: '1px solid', borderColor: 'info.light', borderRadius: 2, bgcolor: 'info.50' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
              שיוך כוללים לחברים אוטומטי
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              מקשר אוטומטי בין שם הכולל הגולמי מהאקסל לבין הכוללים שיובאו למערכת — צריך להריץ אחרי ייבוא הכוללים.
            </Typography>
            <Button
              variant="contained"
              color="info"
              size="small"
              startIcon={<LinkIcon />}
              onClick={handleLinkKolels}
              disabled={linkLoading}
            >
              {linkLoading ? 'משייך...' : 'שייך כוללים לכל החברים'}
            </Button>
            {linkError && <Alert severity="error" sx={{ mt: 1 }}>{linkError}</Alert>}
            {linkResult && (
              <Box mt={1}>
                <Alert severity={linkResult.notFound?.length ? 'warning' : 'success'} sx={{ mb: 0.5 }}>
                  {linkResult.message}
                </Alert>
                {linkResult.notFound?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {linkResult.notFound.map((n) => (
                      <Chip key={n} label={n} size="small" color="warning" variant="outlined" />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        }
      />
    </Box>
  );
};

export default KolelsList;
